<template>
  <div class="modal-preview-wrap">
    <div v-if="chartKind === 'bar' && symbolList.length" class="symbols-row">
      <div v-for="sym in symbolList" :key="sym.name" class="sym-item">
        <span class="sym-dot" :style="{ background: sym.color }" />
        <span class="sym-label">{{ sym.name }}</span>
      </div>
    </div>
    <div ref="chartEl" class="chart-el" />
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import * as echarts from 'echarts'
import { getMinutesCountByTime, getTimeByMinutesCount } from '@/utils/timeHelper/index.js'

const $str = window.$str

const props = defineProps({
  /** 'bar'：与列表卡片一致的阶梯柱状；'line'：现货 15 分钟折线 */
  chartKind: {
    type: String,
    default: 'bar',
  },
  /** 与 electrovalenceCard 一致：{ priceDetails, chargeType } */
  barPriceInfo: {
    type: Object,
    default: () => ({ priceDetails: [], chargeType: 2 }),
  },
  /** 现货曲线：[{ time: 'HH:mm', value: number }]，时间点间隔由数据决定 */
  lineValues: {
    type: Array,
    default: () => [],
  },
})

const chartEl = ref(null)
let chartInst = null

const symbolList = computed(() => {
  const res = []
  const list = props.barPriceInfo?.priceDetails ?? []
  if (!Array.isArray(list)) return res
  list.forEach((it) => {
    if (!res.some((s) => s.name === it.name)) {
      res.push({ name: it.name, color: it.color })
    }
  })
  return res
})

function buildBarOption() {
  const infoItems = props.barPriceInfo?.priceDetails ?? []
  const chartData = infoItems.map((item) => {
    const st = getMinutesCountByTime(item.startTime)
    const et = getMinutesCountByTime(item.endTime)
    return {
      value: [st, et, item.price, item.name],
      itemStyle: {
        color: echarts.color.modifyAlpha(item.color, 0.5),
      },
    }
  })

  const lineColor = '#EDF2F6'
  const textColor = '#96A5B8'

  return {
    grid: { left: '5%', right: '3%', bottom: '10%', top: '15%' },
    xAxis: {
      type: 'value',
      min: 0,
      max: 1440,
      interval: 6 * 60,
      axisLabel: {
        color: textColor,
        formatter: (value) => getTimeByMinutesCount(value),
      },
      splitLine: { show: true, lineStyle: { color: lineColor } },
    },
    yAxis: {
      type: 'value',
      name: $str.getKey('收益管理/价格(元/kWh)'),
      nameTextStyle: { color: textColor },
      axisLabel: { color: textColor },
      splitLine: { show: true, lineStyle: { color: lineColor } },
    },
    series: [
      {
        type: 'custom',
        data: chartData,
        renderItem: (params, api) => {
          const yValue = api.value(2)
          const start = api.coord([api.value(0), yValue])
          const size = api.size([api.value(1) - api.value(0), yValue])
          return {
            type: 'rect',
            shape: {
              x: start[0],
              y: start[1],
              width: size[0],
              height: size[1] || 10,
            },
            style: size[1]
              ? api.style()
              : { fill: 'transparent', opacity: 0 },
          }
        },
        label: { show: true, position: 'top' },
        dimensions: [
          $str.getKey('收益管理/开始时间'),
          $str.getKey('收益管理/结束时间'),
          $str.getKey('收益管理/电价'),
          $str.getKey('收益管理/电价名称'),
        ],
        encode: { x: [0, 1], y: 2, tooltip: [0, 1, 2], itemName: 3 },
      },
    ],
    tooltip: {
      show: true,
      trigger: 'item',
      className: 'chart-tooltip',
      backgroundColor: '#ffffff',
      borderColor: 'rgba(15, 23, 42, 0.06)',
      hideDelay: 0,
      formatter: (params) => {
        if (!params) return ''
        const start_time = getTimeByMinutesCount(params.value[0])
        const end_time = getTimeByMinutesCount(params.value[1])
        const price = params.value[2]
        return `
          <div class="chart-tooltip__time">
            <div class="chart-tooltip__point" style="background: ${params.color}"></div>
            ${params.name}
          </div>
          <div class="chart-tooltip__items-container">
            <div class="chart-tooltip__series">
              <div class="chart-tooltip__series-name">${$str.getKey('收益管理/开始时间')}</div>
              <div class="chart-tooltip__value">${start_time}</div>
            </div>
            <div class="chart-tooltip__series">
              <div class="chart-tooltip__series-name">${$str.getKey('收益管理/结束时间')}</div>
              <div class="chart-tooltip__value">${end_time}</div>
            </div>
            <div class="chart-tooltip__series">
              <div class="chart-tooltip__series-name">${$str.getKey('收益管理/电价')}</div>
              <div class="chart-tooltip__value">${price}</div>
            </div>
          </div>
        `
      },
    },
  }
}

function buildLineOption() {
  const vals = Array.isArray(props.lineValues) ? props.lineValues : []
  // 间隔由数据决定（15 分钟 96 点 / 1 小时 24 点 …），按相邻时间点之差算区间宽度
  const points = vals
    .map((it) => {
      const time = typeof it === 'object' ? it?.time : null
      const value = typeof it === 'object' ? +it?.value : +it
      return { min: time ? getMinutesCountByTime(time) : null, value: Number.isFinite(value) ? value : 0 }
    })
    .filter((it) => it.min !== null)
    .sort((a, b) => a.min - b.min)

  const data = points.map((p, i) => {
    const next = i + 1 < points.length ? points[i + 1].min : 1440
    // 打点落在区间中点，与阶梯柱状的视觉口径一致
    return [p.min + (next - p.min) / 2, p.value]
  })
  const slotMinutes = points.map((p, i) => (i + 1 < points.length ? points[i + 1].min : 1440) - p.min)

  const lineColor = '#EDF2F6'
  const textColor = '#96A5B8'
  const lineMain = '#1862F5'

  return {
    grid: { left: '5%', right: '3%', bottom: '10%', top: '15%' },
    xAxis: {
      type: 'value',
      min: 0,
      max: 1440,
      interval: 6 * 60,
      axisLabel: {
        color: textColor,
        formatter: (value) => getTimeByMinutesCount(value),
      },
      splitLine: { show: true, lineStyle: { color: lineColor } },
    },
    yAxis: {
      type: 'value',
      name: $str.getKey('收益管理/价格(元/kWh)'),
      nameTextStyle: { color: textColor },
      axisLabel: { color: textColor },
      splitLine: { show: true, lineStyle: { color: lineColor } },
    },
    series: [
      {
        type: 'line',
        smooth: false,
        showSymbol: true,
        symbolSize: 4,
        lineStyle: { color: lineMain, width: 2 },
        itemStyle: { color: lineMain },
        data,
      },
    ],
    tooltip: {
      trigger: 'axis',
      className: 'chart-tooltip',
      backgroundColor: '#ffffff',
      borderColor: 'rgba(15, 23, 42, 0.06)',
      formatter: (items) => {
        const p = items?.[0]
        if (!p) return ''
        const idx = p.dataIndex ?? 0
        const width = slotMinutes[idx] ?? 15
        const min = Math.round((p.value[0] ?? 0) - width / 2)
        const st = getTimeByMinutesCount(min)
        const et = min + width >= 1440 ? '24:00' : getTimeByMinutesCount(min + width)
        return `${st} ~ ${et}<br/>${$str.getKey('收益管理/电价')}：${p.value[1]}`
      },
    },
  }
}

function resize() {
  chartInst?.resize()
}

function render() {
  if (!chartEl.value) return
  if (!chartInst) {
    chartInst = echarts.init(chartEl.value)
  }
  const opt = props.chartKind === 'line' ? buildLineOption() : buildBarOption()
  chartInst.setOption(opt, true)
}

watch(
  () => [props.chartKind, props.barPriceInfo, props.lineValues],
  () => {
    nextTick(render)
  },
  { deep: true }
)

onMounted(() => {
  nextTick(render)
  window.addEventListener('resize', resize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resize)
  chartInst?.dispose()
  chartInst = null
})

defineExpose({ resize, render })
</script>

<style lang="scss" scoped>
.modal-preview-wrap {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.symbols-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 0 4px;
}

.sym-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #444a6d;
}

.sym-dot {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.chart-el {
  width: 100%;
  flex: 1;
  min-height: 0;
}
</style>
